/**
 * vonique-animations.js
 * Lightweight scroll animation engine using Intersection Observer.
 * Supports: .vonique-fade-up, .vonique-fade-in, .vonique-slide-in,
 *           .vonique-slide-in-right, .vonique-zoom-in
 * Also handles legacy [data-vonique-anim] attribute system.
 */

class VoniqueAnimations {
  constructor() {
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.initScrollAnimations();
    this.initLegacyAos();
    this.initParallax();
  }

  /**
   * Class-based animation system
   * Add any of the animation classes + .vonique-anim-ready to an element.
   * The engine will add .vonique-anim-done when it enters the viewport.
   */
  initScrollAnimations() {
    if (this.prefersReducedMotion) {
      document.querySelectorAll('.vonique-anim-ready').forEach(el => {
        el.classList.add('vonique-anim-done');
      });
      return;
    }

    const animClasses = [
      'vonique-fade-up',
      'vonique-fade-in',
      'vonique-slide-in',
      'vonique-slide-in-right',
      'vonique-zoom-in'
    ];

    const targets = document.querySelectorAll('.vonique-anim-ready');
    if (!targets.length) return;

    // Add stagger delay to siblings inside a grid/list
    document.querySelectorAll('.vonique-stagger-children').forEach(parent => {
      [...parent.children].forEach((child, i) => {
        const delay = Math.min(i * 100, 500);
        child.classList.add('vonique-anim-ready', 'vonique-fade-up');
        child.style.transitionDelay = delay + 'ms';
      });
    });

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('vonique-anim-done');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });

    document.querySelectorAll('.vonique-anim-ready').forEach(el => observer.observe(el));
  }

  /**
   * Legacy: support [data-vonique-anim] attribute system
   * (used on hero, about, testimonials sections)
   */
  initLegacyAos() {
    if (this.prefersReducedMotion) {
      document.querySelectorAll('[data-vonique-anim]').forEach(el => {
        el.classList.add('vonique-is-visible');
      });
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('vonique-is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -30px 0px' });

    document.querySelectorAll('[data-vonique-anim]').forEach(el => observer.observe(el));
  }

  /**
   * Lightweight parallax on elements with [data-vonique-parallax]
   * Value is speed multiplier (0.1–0.5 recommended)
   */
  initParallax() {
    if (this.prefersReducedMotion) return;

    const parallaxEls = document.querySelectorAll('[data-vonique-parallax]');
    if (!parallaxEls.length) return;

    const onScroll = () => {
      const scrollY = window.scrollY;
      parallaxEls.forEach(el => {
        const speed  = parseFloat(el.dataset.voniqueParallax) || 0.15;
        const rect   = el.getBoundingClientRect();
        const center = rect.top + rect.height / 2 - window.innerHeight / 2;
        el.style.transform = `translateY(${center * speed}px)`;
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll(); // run once on load
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.voniqueAnimations = new VoniqueAnimations();
});
